import { z } from 'zod';
import { LIMITS } from '../registries/limits.ts';
import { POSTER_SOURCES, TRACK_KINDS, TRACK_SOURCES } from '../registries/media.ts';
import { ASSET_STATUSES, MEDIA_FAILURE_CODES, TRACK_STATUSES } from '../registries/statuses.ts';
import { DurationMs, Id, IsoDateTime, LanguageCode, Title } from './primitives.ts';

export const AssetStatusSchema = z.enum(ASSET_STATUSES);
export const MediaFailureCodeSchema = z.enum(MEDIA_FAILURE_CODES);
export const TrackKindSchema = z.enum(TRACK_KINDS);
export const TrackSourceSchema = z.enum(TRACK_SOURCES);
export const TrackStatusSchema = z.enum(TRACK_STATUSES);
export const PosterSourceSchema = z.enum(POSTER_SOURCES);

/** Width divided by height of the poster image, so clients can reserve its exact shape. */
export const AspectRatio = z.number().positive();

export const TrackLabel = z.string().trim().min(LIMITS.trackLabel.min).max(LIMITS.trackLabel.max);

export const AssetSummary = z.object({
  id: Id,
  title: z.string(),
  status: AssetStatusSchema,
  /** Processing progress in [0, 1] while status is `processing`, otherwise null. */
  progress: z.number().min(0).max(1).nullable(),
  failureCode: MediaFailureCodeSchema.nullable(),
  durationMs: DurationMs.nullable(),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
  /** Same-origin URL; readable with the media grant cookie. Null until processed. */
  posterUrl: z.string().nullable(),
  /** Null while there is no poster. */
  posterSource: PosterSourceSchema.nullable(),
  posterAspectRatio: AspectRatio.nullable(),
  sourceFilename: z.string(),
  sourceSizeBytes: z.number().int().nonnegative(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type AssetSummary = z.infer<typeof AssetSummary>;

export const Track = z.object({
  id: Id,
  kind: TrackKindSchema,
  source: TrackSourceSchema,
  status: TrackStatusSchema,
  failureCode: MediaFailureCodeSchema.nullable(),
  language: LanguageCode.nullable(),
  label: z.string().nullable(),
  isDefault: z.boolean(),
});
export type Track = z.infer<typeof Track>;

export const Rendition = z.object({
  height: z.number().int().positive(),
  width: z.number().int().positive(),
  bandwidth: z.number().int().positive(),
});

export const AssetDetail = AssetSummary.extend({
  renditions: z.array(Rendition),
  audioTracks: z.array(Track),
  subtitleTracks: z.array(Track),
  /** Open screenings that use this film; a film in use cannot be deleted. */
  usedBy: z.array(z.object({ screeningId: Id, title: z.string() })),
});
export type AssetDetail = z.infer<typeof AssetDetail>;

export const CreateAssetBody = z.object({
  title: Title,
  filename: z.string().trim().min(1).max(255),
  sizeBytes: z.number().int().positive(),
});

export const AssetCreated = z.object({
  asset: AssetSummary,
  /** Tus endpoint and metadata the client must send with the upload. */
  upload: z.object({ endpoint: z.string(), metadata: z.record(z.string(), z.string()) }),
});
export type AssetCreated = z.infer<typeof AssetCreated>;

export const UpdateAssetBody = z.object({ title: Title });

export const UpdateTrackBody = z
  .object({
    label: TrackLabel.nullable(),
    language: LanguageCode.nullable(),
    isDefault: z.boolean(),
  })
  .partial()
  .refine((body) => Object.keys(body).length > 0, { message: 'Nothing to update' });

/**
 * Multipart fields for `uploadSubtitle`: a `file` part plus these optional text fields.
 * Validated by the server after the multipart stream is parsed.
 */
export const UploadSubtitleFields = z.object({
  language: LanguageCode.optional(),
  label: TrackLabel.optional(),
});
